import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("processing"); // processing | paid | failed
  const [credits, setCredits] = useState(0);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setCredits(data.credits || 0);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (_) {
        /* keep polling */
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("failed");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto flex max-w-md flex-col items-center px-5 py-24 text-center">
        {state === "processing" && (
          <>
            <Loader2 className="h-14 w-14 animate-spin text-primary" />
            <h1 className="mt-6 font-heading text-2xl font-semibold text-foreground" data-testid="pay-processing">{t("pay_processing")}</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="grid h-16 w-16 place-items-center rounded-3xl bg-secondary/20 text-secondary">
              <CheckCircle2 className="h-9 w-9" strokeWidth={1.75} />
            </span>
            <h1 className="mt-6 font-heading text-3xl font-semibold text-foreground" data-testid="pay-success">{t("pay_success_title")}</h1>
            <p className="mt-2 text-muted-foreground">{t("pay_success_text")}</p>
            <p className="mt-4 rounded-full bg-primary/10 px-5 py-2 font-heading text-lg font-semibold text-primary">
              +{credits} {t("credits_added")}
            </p>
            <Button asChild className="mt-8 rounded-full bg-primary px-7 font-semibold text-primary-foreground hover:bg-primary-hover">
              <Link to="/billing" data-testid="back-to-billing">{t("back_to_billing")}</Link>
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <span className="grid h-16 w-16 place-items-center rounded-3xl bg-destructive/10 text-destructive">
              <XCircle className="h-9 w-9" strokeWidth={1.75} />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-semibold text-foreground" data-testid="pay-failed">{t("pay_failed")}</h1>
            <Button asChild variant="outline" className="mt-8 rounded-full px-7 font-semibold">
              <Link to="/billing" data-testid="back-to-billing">{t("back_to_billing")}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
